'use client';

import { nextFreshState, REVALIDATE_INTERVAL_MS } from '@/lib/next/entryFresh.mjs';
import { ENTRY_ROUTE } from '@/lib/next/entryUri.mjs';
import { type ReactNode, useEffect, useRef, useState } from 'react';

export type EntryRevalidatorProps<TEntry> = {
  entry: TEntry;
  uri: string;
  enabled?: boolean;
  children: (entry: TEntry) => ReactNode;
};

export default function EntryRevalidator<TEntry>({
  entry: baked,
  uri,
  enabled = true,
  children,
}: EntryRevalidatorProps<TEntry>) {
  const [entry, setEntry] = useState<TEntry>(baked);
  const shown = useRef<TEntry>(baked);
  const lastStamp = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let controller: AbortController | undefined;

    const revalidate = () => {
      controller?.abort();
      controller = new AbortController();

      fetch(`${ENTRY_ROUTE}?uri=${encodeURIComponent(uri)}`, {
        signal: controller.signal,
        credentials: 'omit',
        cache: 'no-store',
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((answer: { entry?: TEntry; fetchedAt?: number } | null) => {
          if (!answer?.entry) return;

          const next = nextFreshState(
            { current: shown.current, lastStamp: lastStamp.current },
            answer
          );

          lastStamp.current = next.lastStamp;

          if (next.current !== shown.current) {
            shown.current = next.current;
            setEntry(next.current);
          }
        })
        .catch(() => undefined);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    revalidate();

    const timer = window.setInterval(onVisible, REVALIDATE_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller?.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uri, enabled, baked]);

  return <>{children(entry)}</>;
}
